import React, {
    ReactElement, useState, KeyboardEvent, useEffect,
} from "react";
import { RiCloseCircleFill } from "@remixicon/react";
import Styles from "./TagList.module.scss";
import { useTheme } from "../../contexts/Theme";
import FormGroup from "../theme/form-group/FormGroup";
import Button from "../theme/button/Button";
import Badge from "../theme/badge/Badge";

export interface TagListProps {
    value?: string[];
    onChange?: (tags: string[]) => void;
    isTag?: boolean;
    canNotHaveDuplicates?: boolean;
    placeholder?: string;
    type?: string;
    shouldSort?: boolean;
    className?: string;
}

export function normalizeTag(tag: string): string {
    return tag.replace(/\s/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toSnakeCase();
}

export default function TagList({
    value, onChange, isTag, canNotHaveDuplicates, placeholder, type, shouldSort, className,
}: TagListProps): ReactElement {
    const [currentTag, setCurrentTag] = useState<string | undefined>("");
    const [tags, setTags] = useState<string[]>([]);
    const ref = React.createRef<HTMLInputElement>();
    const theme = useTheme();

    useEffect(() => {
        if (value && !tags.equals(value)) {
            setTags(value ?? []);
        }
    }, [value]);

    const add = () => {
        if (!currentTag || currentTag.length === 0) {
            return;
        }

        if (type !== "text" && !ref.current?.checkValidity()) {
            ref.current?.reportValidity();
            return;
        }

        let tag = currentTag;
        if (isTag) {
            tag = normalizeTag(tag);
        }
        let newTags = (tags ?? []).concat([tag]);
        if (canNotHaveDuplicates) {
            newTags = newTags.dedupe();
        }
        if (shouldSort) {
            newTags = newTags.sortBy(row => row);
        }
        setTags(newTags);
        onChange?.(newTags);
        setCurrentTag("");
    };
    const remove = (tag: string) => {
        const copy = Array.from(tags);
        const index = copy.findIndex(current => current === tag);
        copy.splice(index, 1);
        setTags(copy);
        onChange?.(copy);
    };

    const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            add();
        }
    };

    let tagList = tags;
    if (shouldSort) {
        tagList = tagList.sortBy(row => row);
    }

    if (theme.isV3) {
        return (
            <div className={className}>
                <div className={Styles["input-group"]}>
                    <FormGroup
                        ref={ref}
                        onKeyDown={onKeyPress}
                        size="small"
                        onChange={evt => setCurrentTag(evt.target.value)}
                        value={currentTag}
                        placeholder={placeholder ?? "Add a tag"}
                        type={type}
                    />
                    <Button size="small" variant="primary" onClick={add}>
                        Add
                    </Button>
                </div>
                {tagList?.isNotEmpty && (
                    <div className={Styles["tag-list"]}>
                        {tagList.map(tag => (
                            <Badge
                                variant="primary"
                                key={tag}
                            >
                                {isTag ? "#" : ""}
                                {tag}
                                {" "}
                                <Button
                                    prefixIcon={<RiCloseCircleFill />}
                                    size="xsmall"
                                    variant="ghost-gray"
                                    aria-label="Close"
                                    onClick={() => remove(tag)}
                                />
                            </Badge>
                        ))}
                    </div>
                )}
            </div>
        );
    }
    return (

        <div className="bg-white border rounded">
            <div className={`d-flex ${tagList.isNotEmpty ? "border-bottom" : ""}`}>
                <input
                    ref={ref}
                    type={type}
                    className={`form-control border-0 bg-none ${Styles.input}`}
                    onKeyPress={onKeyPress}
                    onChange={evt => setCurrentTag(evt.target.value)}
                    value={currentTag}
                    placeholder={placeholder ?? "Add a tag"}
                />
                <button
                    type="button"
                    onClick={add}
                    className={`btn btn-sm btn-secondary py-1 ${tagList.isEmpty
                        ? Styles["button-border-no-children"]
                        : Styles["button-border"]}`}
                >
                    Add
                </button>
            </div>

            {tagList?.isNotEmpty && (
                <div className="p-3">
                    {tagList.map(tag => (
                        <div className="bg-gray-100 p-1 me-2 rounded d-inline-flex align-items-center mb-2" key={tag}>

                            <code className="flex-grow-1">
                                {isTag ? "#" : ""}
                                {tag}
                            </code>
                            <button
                                type="button"
                                className="btn-close btn-sm ms-2 p-0 m-0"
                                aria-label="Close"
                                onClick={() => remove(tag)}
                            />
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
}

TagList.defaultProps = {
    type: "text",

    value: undefined,
    onChange: undefined,
    isTag: undefined,
    canNotHaveDuplicates: undefined,
    placeholder: undefined,
    shouldSort: undefined,
    className: undefined,
};
